/**
 * PublisherEditor — TipTap-based rich text editor for the SocialPulse
 * blog. Supports:
 *   • Text formatting (bold, italic, headings, lists, blockquote, code)
 *   • Inline links + external anchors
 *   • Images with left / center / right alignment + text wrap
 *   • YouTube video embed (paste-any-URL)
 *   • Link cards (via /publisher/v1/unfurl)
 *   • Text alignment
 *
 * Ships opinionated CSS so images with the `.pub-align-left/right/center`
 * classes behave correctly inside a WYSIWYG surface AND on the read-side
 * article render.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Youtube from "@tiptap/extension-youtube";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo, Redo, Link as LinkIcon,
  ImageIcon, AlignLeft, AlignCenter, AlignRight, Youtube as YoutubeIcon,
  Sparkles, Camera, Users,
  CornerUpLeft, CornerUpRight, CornerDownLeft, CornerDownRight, Type,
  Languages, ChevronDown, Loader2,
} from "lucide-react";
import UnsplashPicker from "./UnsplashPicker";
import PhotoPulsePicker from "./PhotoPulsePicker";
import { TransliterateExtension } from "./TransliterateExtension";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
const I18N_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/i18n`;

// Supported Indic scripts for live transliteration inside the editor.
// Keep this list in sync with <TransliterateInput /> and the backend
// /i18n/transliterate proxy so users get a consistent picker everywhere.
const TRANSLIT_LANGS = [
  { code: "off", label: "Off", native: "English", short: "EN" },
  { code: "hi",  label: "Hindi",     native: "हिन्दी",  short: "HI" },
  { code: "ml",  label: "Malayalam", native: "മലയാളം", short: "ML" },
  { code: "ta",  label: "Tamil",     native: "தமிழ்",   short: "TA" },
  { code: "te",  label: "Telugu",    native: "తెలుగు",  short: "TE" },
  { code: "kn",  label: "Kannada",   native: "ಕನ್ನಡ",   short: "KN" },
  { code: "bn",  label: "Bengali",   native: "বাংলা",   short: "BN" },
  { code: "mr",  label: "Marathi",   native: "मराठी",   short: "MR" },
  { code: "gu",  label: "Gujarati",  native: "ગુજરાતી", short: "GU" },
  { code: "pa",  label: "Punjabi",   native: "ਪੰਜਾਬੀ",   short: "PA" },
  { code: "or",  label: "Odia",      native: "ଓଡ଼ିଆ",   short: "OR" },
  { code: "as",  label: "Assamese",  native: "অসমীয়া", short: "AS" },
  { code: "ur",  label: "Urdu",      native: "اردو",    short: "UR" },
];
const TRANSLIT_PREF_KEY = "sp:input-lang";
const TRANSLIT_SUPPORTED_CODES = new Set(TRANSLIT_LANGS.map((l) => l.code));

/**
 * Custom image extension with an `align` attribute → CSS class so the
 * article body reads well on the reader side even without a JS runtime.
 *
 * Supported alignments (all reader-safe via `.pub-align-<align>` CSS):
 *   • left, center, right         — classic wrap or full-width
 *   • corner-tl, corner-tr        — float pinned to top-left/right
 *   • corner-bl, corner-br        — float pinned to bottom-left/right
 *   • dropcap                     — centered float that acts as a
 *                                    magazine-style lead illustration
 */
const ALIGN_VALUES = new Set([
  "left", "center", "right",
  "corner-tl", "corner-tr", "corner-bl", "corner-br",
  "dropcap",
]);
const AlignedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: (el) => {
          const val = el.getAttribute("data-align") || "center";
          return ALIGN_VALUES.has(val) ? val : "center";
        },
        renderHTML: (attrs) => ({
          "data-align": attrs.align,
          class: `pub-align-${attrs.align}`,
        }),
      },
    };
  },
});

function ToolbarBtn({ onClick, active, disabled, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={`w-8 h-8 rounded-md flex items-center justify-center text-sm transition-colors ${
        active
          ? "bg-blue-600 text-white"
          : "bg-transparent text-slate-700 hover:bg-slate-100"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-6 bg-slate-200 mx-1" />;
}

export default function PublisherEditor({
  value,
  onChange,
  placeholder = "Tell your story…",
  testIdPrefix = "publisher-editor",
  authToken = null, // required for /unfurl (auth-protected)
  preferredTranslitLang = null, // e.g. "ml" when the writer opens the Malayalam translation tab
}) {
  const [unsplashOpen, setUnsplashOpen] = useState(false);
  const [photoPulseOpen, setPhotoPulseOpen] = useState(false);

  // Live transliteration inside the TipTap body. Preference precedence:
  //   1. Explicit prop from the parent (translation tab language)
  //   2. Last-used language remembered in localStorage
  //   3. Off (writer types Latin script as-is)
  const [translitLang, setTranslitLang] = useState(() => {
    if (preferredTranslitLang && TRANSLIT_SUPPORTED_CODES.has(preferredTranslitLang)) {
      return preferredTranslitLang;
    }
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(TRANSLIT_PREF_KEY);
      if (saved && TRANSLIT_SUPPORTED_CODES.has(saved)) return saved;
    }
    return "off";
  });
  const [translitBusy, setTranslitBusy] = useState(false);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const translitLangRef = useRef(translitLang);

  useEffect(() => {
    translitLangRef.current = translitLang;
    if (typeof window !== "undefined" && translitLang) {
      window.localStorage.setItem(TRANSLIT_PREF_KEY, translitLang);
    }
  }, [translitLang]);

  // When the parent switches translation tabs, follow their explicit
  // choice. If the new tab is an Indic script we support, live-convert
  // to it; otherwise (English source, Spanish, French, Arabic, …) turn
  // conversion OFF so the writer never has Latin words auto-swapped on
  // the English tab because they were previously editing Malayalam.
  useEffect(() => {
    if (!preferredTranslitLang) return;
    if (TRANSLIT_SUPPORTED_CODES.has(preferredTranslitLang)) {
      setTranslitLang(preferredTranslitLang);
    } else {
      setTranslitLang("off");
    }
  }, [preferredTranslitLang]);

  const currentTranslitMeta = useMemo(
    () => TRANSLIT_LANGS.find((l) => l.code === translitLang) || TRANSLIT_LANGS[0],
    [translitLang],
  );

  const transliterateWord = useCallback(async (word, lang) => {
    try {
      const r = await fetch(`${I18N_BASE}/transliterate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ text: word, target_lang: lang }),
      });
      if (!r.ok) return word;
      const data = await r.json();
      return data?.transliterated || word;
    } catch (_e) {
      return word;
    }
  }, [authToken]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener nofollow", target: "_blank" },
      }),
      AlignedImage,
      Youtube.configure({ inline: false, controls: true, nocookie: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      TransliterateExtension.configure({
        getLang: () => translitLangRef.current,
        transliterate: transliterateWord,
        onBusy: setTranslitBusy,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  // Sync incoming value (e.g. language switch reloading translated body)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value != null && value !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt(
      "Image URL (paste any public image URL — Emergent Object Storage upload is next-phase)",
    );
    if (!url) return;
    const alt = window.prompt("Alt text (short description for accessibility)", "") || "";
    editor.chain().focus()
      .setImage({ src: url, alt, align: "center" })
      .createParagraphNear()
      .run();
  }, [editor]);

  const insertUnsplashPhoto = useCallback((photo) => {
    if (!editor || !photo) return;
    // Build a figure with photographer attribution baked in — reader
    // side gets the credit line for free, keeping us compliant with
    // Unsplash's API guidelines.
    const isPP = photo.source === "photopulse";
    const credit = isPP
      ? (photo.photographer?.name
        ? `Photo by <a href="${photo.photographer?.profile_url || "#"}" target="_blank" rel="noopener nofollow">${escapeHtml(photo.photographer.name)}</a> on <a href="https://socialpulse.com/photopulse" target="_blank" rel="noopener nofollow">PhotoPulse</a>`
        : `Community photo via <a href="https://socialpulse.com/photopulse" target="_blank" rel="noopener nofollow">PhotoPulse</a>`)
      : `Photo by <a href="${photo.photographer?.profile_url || "#"}" target="_blank" rel="noopener nofollow">${escapeHtml(photo.photographer?.name || "Unsplash")}</a> on <a href="${photo.unsplash_link}" target="_blank" rel="noopener nofollow">Unsplash</a>`;
    const alt = escapeHtml(photo.alt_description || "");
    const figureCls = isPP ? "pub-photopulse-figure" : "pub-unsplash-figure";
    const captionCls = isPP ? "pub-photopulse-credit" : "pub-unsplash-credit";
    const dataAttr = isPP
      ? `data-photopulse-id="${escapeHtml(photo.photo_id)}"`
      : `data-unsplash-id="${escapeHtml(photo.photo_id)}"`;
    const html = `<figure class="${figureCls}" ${dataAttr}>
  <img src="${escapeHtml(photo.image_url)}" alt="${alt}" data-align="center" class="pub-align-center" loading="lazy" />
  <figcaption class="${captionCls}">${credit}</figcaption>
</figure>`;
    editor.chain().focus().insertContent(html).createParagraphNear().run();
  }, [editor]);

  const setImageAlign = useCallback((align) => {
    if (!editor) return;
    editor.chain().focus().updateAttributes("image", { align }).run();
  }, [editor]);

  const insertYoutube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("YouTube URL");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url, width: 640, height: 360 }).run();
  }, [editor]);

  const insertLinkCard = useCallback(async () => {
    if (!editor) return;
    const url = window.prompt(
      "URL to expand into a card (article, YouTube, Spotify, any page with Open Graph tags)",
    );
    if (!url) return;
    try {
      const r = await fetch(`${BASE}/unfurl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ url }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const html = renderLinkCard(data);
      editor.chain().focus().insertContent(html).createParagraphNear().run();
    } catch (e) {
      window.alert(`Could not expand link: ${e.message}`);
    }
  }, [editor, authToken]);

  if (!editor) return null;

  const imgSelected = editor.isActive("image");

  return (
    <div className="border border-slate-300 rounded-2xl bg-white overflow-hidden" data-testid={testIdPrefix}>
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-2 sticky top-0 z-10"
        data-testid={`${testIdPrefix}-toolbar`}
      >
        <ToolbarBtn testId={`${testIdPrefix}-bold`} title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-italic`} title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-strike`} title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}><Strikethrough className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-code`} title="Inline code"
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}><Code className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-h1`} title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}><Heading1 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-h2`} title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-h3`} title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}><Heading3 className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-ul`} title="Bulleted list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-ol`} title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-quote`} title="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}><Quote className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-align-left`} title="Align left"
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          active={editor.isActive({ textAlign: "left" })}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-align-center`} title="Align center"
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          active={editor.isActive({ textAlign: "center" })}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-align-right`} title="Align right"
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          active={editor.isActive({ textAlign: "right" })}><AlignRight className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-link`} title="Insert link"
          onClick={setLink}
          active={editor.isActive("link")}><LinkIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-image`} title="Insert image URL"
          onClick={insertImage}><ImageIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-unsplash`} title="Browse Unsplash photos"
          onClick={() => setUnsplashOpen(true)}><Camera className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-photopulse`} title="Browse PhotoPulse community photos"
          onClick={() => setPhotoPulseOpen(true)}><Users className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-youtube`} title="Embed YouTube video"
          onClick={insertYoutube}><YoutubeIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-linkcard`} title="Expand a link into a rich card"
          onClick={insertLinkCard}><Sparkles className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        {imgSelected && (
          <>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold pr-1">
              Image
            </span>
            <ToolbarBtn testId={`${testIdPrefix}-img-left`} title="Wrap left"
              onClick={() => setImageAlign("left")}
              active={editor.getAttributes("image").align === "left"}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-center`} title="Center"
              onClick={() => setImageAlign("center")}
              active={editor.getAttributes("image").align === "center"}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-right`} title="Wrap right"
              onClick={() => setImageAlign("right")}
              active={editor.getAttributes("image").align === "right"}><AlignRight className="w-4 h-4" /></ToolbarBtn>
            <Divider />
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-tl`} title="Corner: top-left"
              onClick={() => setImageAlign("corner-tl")}
              active={editor.getAttributes("image").align === "corner-tl"}><CornerUpLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-tr`} title="Corner: top-right"
              onClick={() => setImageAlign("corner-tr")}
              active={editor.getAttributes("image").align === "corner-tr"}><CornerUpRight className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-bl`} title="Corner: bottom-left"
              onClick={() => setImageAlign("corner-bl")}
              active={editor.getAttributes("image").align === "corner-bl"}><CornerDownLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-br`} title="Corner: bottom-right"
              onClick={() => setImageAlign("corner-br")}
              active={editor.getAttributes("image").align === "corner-br"}><CornerDownRight className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-dropcap`} title="Drop-cap lead illustration"
              onClick={() => setImageAlign("dropcap")}
              active={editor.getAttributes("image").align === "dropcap"}><Type className="w-4 h-4" /></ToolbarBtn>
            <Divider />
          </>
        )}
        <ToolbarBtn testId={`${testIdPrefix}-undo`} title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}><Undo className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-redo`} title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}><Redo className="w-4 h-4" /></ToolbarBtn>

        {/* Live transliteration chip — mirrors <TransliterateInput /> so
            writers get the same Latin→Indic conversion inside TipTap. */}
        <div className="ml-auto relative flex items-center gap-1">
          {translitBusy && (
            <Loader2
              className="w-3.5 h-3.5 animate-spin text-slate-400"
              data-testid={`${testIdPrefix}-translit-busy`}
            />
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setLangPickerOpen((v) => !v); }}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest shadow-sm hover:bg-slate-50"
            style={{ color: translitLang === "off" ? "#6B7280" : "#B58936" }}
            title={translitLang === "off"
              ? "Type in English (no live conversion). Click to enable Indic script."
              : `Live-converting to ${currentTranslitMeta.native}. Click to change.`}
            data-testid={`${testIdPrefix}-translit-chip`}
          >
            <Languages className="w-3 h-3" />
            {currentTranslitMeta.short}
            <ChevronDown className="w-2.5 h-2.5" />
          </button>
          {langPickerOpen && (
            <div
              className="absolute top-full right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg py-1 max-h-72 overflow-auto z-30"
              style={{ minWidth: 210 }}
              data-testid={`${testIdPrefix}-translit-picker`}
            >
              {TRANSLIT_LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setTranslitLang(l.code); setLangPickerOpen(false); }}
                  className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${translitLang === l.code ? "font-semibold" : ""}`}
                  data-testid={`${testIdPrefix}-translit-picker-${l.code}`}
                >
                  <span className="opacity-60 mr-1.5 text-[10px] uppercase tracking-widest">{l.short}</span>
                  <span className="mr-1.5">{l.native}</span>
                  {l.code !== "off" && (
                    <span className="opacity-40 text-[10px]">· {l.label}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <EditorContent
        editor={editor}
        className="pub-prose min-h-[400px] px-6 py-6"
        data-testid={`${testIdPrefix}-content`}
      />
      <UnsplashPicker
        open={unsplashOpen}
        onClose={() => setUnsplashOpen(false)}
        onPick={insertUnsplashPhoto}
        context="publisher_editor"
      />
      <PhotoPulsePicker
        open={photoPulseOpen}
        onClose={() => setPhotoPulseOpen(false)}
        onPick={insertUnsplashPhoto}
      />
    </div>
  );
}

function escapeHtml(s) {
  return (s || "").replace(/[<>&"']/g, (c) => ({
    "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;",
  }[c]));
}


/**
 * Render a compact link-card HTML block from an unfurl payload.
 * The read-side gets the same static HTML — no JS runtime required
 * to render the card once the article is saved.
 */
function renderLinkCard(data) {
  const safe = (s) => (s || "").replace(/[<>&"']/g, (c) => ({
    "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;",
  }[c]));
  const title = safe(data.title || data.url);
  const desc = safe(data.description || "");
  const image = safe(data.image || "");
  const site = safe(data.site_name || "");
  const url = safe(data.url);
  const favicon = safe(data.favicon || "");
  return `<div class="pub-linkcard" data-kind="${safe(data.kind || "page")}">
  <a href="${url}" target="_blank" rel="noopener nofollow">
    ${image ? `<div class="pub-linkcard-image"><img src="${image}" alt="" loading="lazy" /></div>` : ""}
    <div class="pub-linkcard-body">
      <div class="pub-linkcard-site">
        ${favicon ? `<img src="${favicon}" alt="" class="pub-linkcard-favicon" />` : ""}
        <span>${site}</span>
      </div>
      <div class="pub-linkcard-title">${title}</div>
      ${desc ? `<div class="pub-linkcard-desc">${desc}</div>` : ""}
    </div>
  </a>
</div>`;
}
